import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup } from './Button';

export interface DialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  /** alertdialog for confirmations and warnings. */
  role?: 'dialog' | 'alertdialog';
  actions?: ReactNode;
}

/**
 * Modal dialog on native <dialog> + showModal(): focus is trapped by the browser, Escape closes it,
 * and focus returns to the element that opened it.
 */
export function Dialog({ open, title, children, onClose, role = 'dialog', actions }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const bodyId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handleClose = () => {
      const target = opener.current;
      opener.current = null;
      if (target?.isConnected) target.focus();
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, []);
  return (
    <dialog
      ref={ref}
      className="app-dialog"
      role={role}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      aria-modal="true"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {open && (
        <>
          <h2 className="app-dialog__title" id={titleId}>
            {title}
          </h2>
          <div id={bodyId}>{children}</div>
          {actions}
        </>
      )}
    </dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmText: string;
  cancelText?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation step (e.g. delete, publish, generate report). */
export function ConfirmDialog({ open, title, children, confirmText, cancelText, danger, onConfirm, onCancel }: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      role="alertdialog"
      actions={
        <ButtonGroup>
          <Button variant={danger ? 'warning' : 'primary'} onClick={onConfirm}>
            {confirmText}
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            {cancelText ?? t('common.cancel')}
          </Button>
        </ButtonGroup>
      }
    >
      {children}
    </Dialog>
  );
}
